import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {Bubble, ConfigProvider, Justify, SearchBox, Table, Button, Modal} from 'tea-component';
import {autotip, scrollable, selectable, sortable} from 'tea-component/lib/table/addons';
import {useRequest} from 'ahooks';
import {getLocalText} from '@/pages/datalab/utils/utils';
import {SortableColumn} from 'tea-component/es/table/addons';
import {ReactSortable} from 'react-sortablejs';
import SvgIcon from '@/pages/home/components/SvgIcon';
import '../join_select.scss';
import BubbleEllipsis from "@/pages/datalab/components/BubbleEllipsis";
import {getPersonalDataList} from "@/pages/datalab/pages/Number/PersonalData/service";
import UploadModalBody from "@/pages/datalab/pages/Number/PersonalData/components/UploadModalBody";
import {useIntl} from "umi";
import {LANGUAGE_EN} from "@home/_config/config";
import {IUploadFileRef} from "@/pages/datalab/pages/Number/PersonalData/components/UploadFile";

const sortColumns: SortableColumn[] = [{ key: 'create_time_str', prefer: 'desc' }];
const MAX_TABLE_NUM = 3;

interface Paginator {
  pageIndex: number;
  hasNext: boolean;
  total: number;
}

const PersonalDataList = (props: { gameCode: string; selectTables: any[]; setSelectTables: Function; selfJoin: boolean; setSelfJoin: Function; }) => {
  const {
    gameCode,
    selectTables,
    setSelectTables,
    selfJoin,
    setSelfJoin,
  } = props;
  const { formatMessage, locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  // 创建allStateRef存储所有状态
  const allStateRef = useRef<TAllState>({
    uploadTableFile: null,
    saveTableInfo: null
  });
  // 新增：上传文件组件引用
  const uploadFileRef = useRef<IUploadFileRef>(null);
  const [sort, setSort] = useState<any>({ by: 'create_time_str', order: 'desc' });
  const [checkBoxValue, setCheckBoxValue] = useState<string[]>([]);
  // 搜索框点击搜索按钮后, 触发表格请求的搜索字段
  const [searchWords, setSearchWords] = useState<string | undefined>('');
  const paginatorRef = useRef<Paginator>({pageIndex: 1, hasNext: true, total: 0});

  const requestDataRef = useRef<any>(null);

  const {loading, error, runAsync} = useRequest(getPersonalDataList, {
    manual: true,
  });
  const [records, setRecords] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);

  const buildSelectTable = useCallback(
    (oldValue: string[], newValue: string[]) => {
      if (selfJoin && (newValue?.length === 0 || newValue?.length === 2)) {
        // newValue.length === 0 是取消了自关联的数据表的勾选
        // newValue.length === 2 是勾选了其他数据表.
        // 以上两种情况都要取消自关联状态
        setSelfJoin(false);
        selectTables.pop();
      }
      const removeValues = oldValue.filter((value) => !newValue.includes(value));
      const addValues = newValue.filter((value) => !oldValue.includes(value));
      let newSelect = selectTables.filter((item) => !removeValues.includes(item.table_id));
      const addSelect: any[] = records.filter((item: any) => addValues.includes(item.table_id));
      newSelect = [...newSelect, ...addSelect];
      newSelect = newSelect.slice(Math.max(0, newSelect.length - MAX_TABLE_NUM), newSelect.length);
      setSelectTables(newSelect);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectTables, records],
  );

  const deleteSelectTable = (index: number) => {
    if (index < 0 || index >= selectTables.length) {
      return;
    }
    const newTables = [...selectTables];
    newTables.splice(index, 1);
    setSelectTables(newTables);
  };

  requestDataRef.current = (isAdd: boolean, newSort?: any) => {
    const sortObj = newSort ?? sort;
    const params = {
      game_code: gameCode,
      keywords: searchWords,
      // order_by: sort?.by,
      // desc: sort?.order ? sort?.order === 'desc' : true,
      page: paginatorRef.current.pageIndex,
      page_size: 20,
      sorts: sortObj?.by ? [{
        key: sortObj.by,
        value: sortObj.order
      }] : []
    };

    runAsync(params)
      .then((res) => {
        const dataList = res?.data_list || [];
        setRecords((records) => isAdd ? records.concat(dataList) : dataList);
        paginatorRef.current.total = res?.row_count;
      })
      .catch((e) => {
        console.log(e);
      }).finally(() => {
        paginatorRef.current.hasNext = true;
      });
  };
  
  useEffect(() => {
    paginatorRef.current = {pageIndex: 1, hasNext: true, total: 0};
    requestDataRef.current(false);
  }, [searchWords]);

  useEffect(() => {
    // 主要是想排序，而不是过滤
    let newValue: string[] = [];
    if (checkBoxValue.length > 0) {
      newValue = newValue.concat(checkBoxValue.filter((value) => selectTables.some((item) => item?.table_id === value)));
    }
    newValue = newValue.concat(
      selectTables.filter((item) => !newValue.includes(item?.table_id)).map((item) => item?.table_id),
    );
    setCheckBoxValue(newValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectTables]);

  useEffect(() => {
    const newSelectTables = [...selectTables];
    newSelectTables.forEach((value, index) => {
      const newValue = records?.find((item: any) => item.table_id === value.table_id);
      newSelectTables[index] = newValue || value;
    });
    (JSON.stringify(selectTables) !== JSON.stringify(newSelectTables)) && setSelectTables(newSelectTables);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [records]);

  useLayoutEffect(() => {
    const element = document.querySelector('.add-source-table .tea-table__body');
    const handleScroll = () => {

      // 检查是否滚动到底部
      if (element?.scrollTop + element?.clientHeight >= element?.scrollHeight - 10) {

        if (paginatorRef.current.hasNext && (paginatorRef.current.pageIndex * 20 < paginatorRef.current.total)) {
          paginatorRef.current = {
            pageIndex: paginatorRef.current.pageIndex + 1,
            hasNext: false,
            total: paginatorRef.current.total
          };
          requestDataRef.current(true);
        }

      }
    };

    // 添加滚动监听事件
    element?.addEventListener('scroll', handleScroll);

    return () => {
      element?.removeEventListener('scroll', handleScroll);
    };

  }, []);

  const onlyOneTable = selectTables.length === 1;
  return (
    <>
      <Justify
        left={(<Button onClick={() => setShowUploadModal(true)} className="dbl-personal-data__upload-btn">
          <SvgIcon className='dbl-btn-top-icon' type="upload" />
          {formatMessage({ id: 'dbl_personal_data_upload_data' })}
        </Button>)}
        right={(<SearchBox
          className="add-steep-search"
          multiline={false}
          value={searchWords}
          placeholder={formatMessage({id: 'dbl_personal_data_search'})}
          onPressEnter={(value) => {
            setSearchWords(value);
          }}
          onChange={(value) => {
            setSearchWords(value);
          }}
          onSearch={(value) => {
            setSearchWords(value);
          }}
          onClear={() => {
            setSearchWords('');
          }}
        />)}

      />

      <ConfigProvider locale={isEn ? "en" : "zh"}>
        <Table
          records={records}
          bordered
          className="add-source-table"
          recordKey="table_id"
          columns={[
            {key: 'table_name', header: formatMessage({id: 'dbl_personal_data_name'}), width: 310, render(record){
              return getLocalText(isEn, record?.customize_name, record?.customize_name_en, record?.table_name);
            }},
            {
              key: 'table_desc', header: formatMessage({id: 'dbl_personal_data_description'}), width: 356,
              render: (record) => {
                const content = isEn ? (record?.table_desc_en || record?.table_desc) : record?.table_desc;
                return content ? (<BubbleEllipsis
                  content={content}
                  className={'add-source-table-desc-bubble'}
                />) : '-';
              }
            },
            {
              key: 'create_time_str',
              header: formatMessage({id: 'dbl_personal_data_creation_time'}),
              width: 190,
            },
            // {
            //   key: 'use_count', header: formatMessage("usage_count"), align: 'right', width: 140, render(record) {
            //     return formatNumberByComma(record?.use_count) || '-';
            //   }
            // },

          ]}
          rowDisabled={(record) => record.sync_status === 1}
          addons={[
            // 支持表格滚动，高度超过 192 开始显示滚动条
            scrollable({
              maxHeight: selectTables.length > 0 ?  477 : 522,
            }),
            selectable({
              // 选框放在「消息类型」列上
              targetColumnKey: 'table_name',
              // 禁用全选
              all: false,
              // 已选中的键值
              value: checkBoxValue,
              // 选中键值发生变更
              onChange: (value) => {
                buildSelectTable(checkBoxValue, value);
                setCheckBoxValue(value);
              },
              render: (element, context) => {
                const {rowKey} = context;
                return (selectTables.length < 3 || selectTables.find((item) => item.table_id === rowKey)) ? (
                  element
                ) : (
                  <Bubble dark={true} className={'add-source-table-name-bubble'}
                    content={formatMessage({id: 'dbl_personal_data_select_limit_tip'})}>
                    {element}
                  </Bubble>
                );
              },
              // false=>禁用 true=>开启
              rowSelectable: (rowKey: string) => selectTables.length < 3 || selectTables.find((item) => item.table_id === rowKey),
            }),
            sortable({
              columns: sortColumns,
              value: [sort],
              onChange: (value: any[], context) => {
                // 如果value数组没有元素说明当前是未排序状态, 那么上传给后端的参数就应该是按照创建时间降序排序
                const sort = value?.length > 0 ? context.sort : {};
                setSort(sort);
                paginatorRef.current.pageIndex = 1;
                requestDataRef.current(false, sort);
              },
            }),
            autotip({
              isLoading: loading,
              isError: !!error,
              loadingText: formatMessage({id: 'dbl_common_loading'}),
              errorText: (
                <span>
                  {formatMessage({id: 'dbl_common_load_fail'})}
                </span>
              ),
              emptyText: formatMessage({id: 'dbl_common_no_data'}),
              style: {lineHeight: '482px'},
            }),
          ]}
        />
      </ConfigProvider>

      <ReactSortable
        setList={(newValue: any[]) => {
          setSelectTables(newValue);
        }}
        className="footer-flex join-select-sortable"
        list={selectTables}
        handle={'.data-label-tips'}
        animation={150}
        delay={100}
        forceFallback={true}
        style={{display: `${selectTables.length === 0 ? 'none' : ''}`}}
      >
        {selectTables.map((it, index) => {
          const title = getLocalText(isEn, it?.customize_name, it?.customize_name_en, it?.table_name);
          return (
            <div className="data-label-tips join-mgr-modal-select-cell-container" key={it?.table_id + index}>
              {/* 数据循环时候数据结构在这 */}
              <div className="footer-item">
                {onlyOneTable ? '' : (<div className="footer-num">{`t${index}`}</div>)}
                <div className={`footer-text ${onlyOneTable ? 'footer-not-num' : ''}`} title={title}>
                  {title}
                </div>
                <div
                  onClick={() => {
                    deleteSelectTable(index);
                    // 自关联的情况下, 删除任意表格代表取消自关联状态.
                    selfJoin && setSelfJoin(false);
                  }}
                >
                  <SvgIcon type="clonse" className="footer-icon" />
                </div>
              </div>
            </div>
          );
        } )}
      </ReactSortable>
      
      <Modal
        visible={showUploadModal}
        caption={(<>
          <Button
            // tea-layout 类名下 btnback类型的icon才会渲染黑色的箭头, 否则是蓝色的箭头
            className={'tea-layout'}
            type={'icon'}
            icon={'btnback'}
            onClick={() => {
              // @ts-ignore
              if (['idle', 'error'].includes(uploadFileRef.current?.status)) {
                // 还没有上传文件, 或上传文件失败, 直接关闭弹窗
                setShowUploadModal(false);
                return;
              }
              // 上传文件中, 或上传文件成功弹出弹窗确认
              Modal.confirm({
                message: formatMessage({id: 'dbl_personal_data_close_title'}),
                description: formatMessage({id: 'dbl_personal_data_close_content'}),
                okText: formatMessage({id: 'dbl_common_confirm'}),
                cancelText: formatMessage({id: 'dbl_common_cancel'}),
              }).then((result) => {
                // 用户点击了确认按钮
                // 在关闭前调用deleteFile方法
                if (result) {
                  uploadFileRef.current?.deleteFile();
                  setShowUploadModal(false);
                }
              });
            }}
            style={{
              position: 'static',
              minWidth: '10px',
              width: 16,
              padding: 0,
              marginRight: 10,
              paddingBottom: 3,
              display: 'inline-block',
              height: 28,
            }}/>
          {formatMessage({ id: 'dbl_personal_data_upload_data' })}
        </>)}
        size={'80%'}
        disableCloseIcon={true}
        disableEscape={true}
        className={'dbl-personal-data-modal'}
      >
        <Modal.Body>
          <UploadModalBody
            ref={uploadFileRef}
            style={{
              height: 713
            }}
            allStateRef={allStateRef}
            onClose={() => setShowUploadModal(false)}
            onSaveSuccess={() => {
              setSort({ by: 'create_time_str', order: 'desc' });
              paginatorRef.current.pageIndex = 1;
              requestDataRef.current(false, { by: 'create_time_str', order: 'desc' });
            }}
          />
        </Modal.Body>
      </Modal>
    </>
  );
};

export default PersonalDataList;